import { alpha } from '@mui/material/styles';
import { coresHex, clarear, escurecer } from '../styles/styleUtils';

// ----------------------------------------------------------------------
// TriAxis — Paleta do tema MUI
//
// As cores de marca vêm de `coresHex` (styles/styleUtils), a mesma fonte
// usada pelas telas. Os tons claros/escuros de cada família são DERIVADOS
// por clarear()/escurecer() — trocar a cor da empresa reflete em todos.
//
// IMPORTANTE: as famílias que dependem da marca são montadas dentro de
// `criarPalette()`, e não em constantes de módulo. Constantes seriam
// avaliadas uma única vez, no import, e ficariam congeladas nos valores
// padrão — o tema nunca acompanharia a paleta da empresa.
//
// Regra 60-30-10:
//   60% → cor escura + branco — base dominante (palette.primary)
//   30% → cor neutra          — apoio e elementos secundários
//   10% → cor primária        — destaque, CTAs e ações (palette.secondary)
// ----------------------------------------------------------------------

// GREY — escala neutra mantida para compatibilidade MUI (independe da marca)
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

// ── Cores semânticas: NÃO acompanham a marca (regra 3 do padrão visual) ──

// INFO — Azul informativo (notificações/sistema)
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff',
};

// SUCCESS — Verde (status de sucesso)
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: GREY[800],
};

// WARNING — Amarelo/âmbar (alertas)
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800],
};

// ERROR — Vermelho (erros)
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff',
};

// LIGHTGREEN — status operacionais
const LIGHTGREEN = {
  lighter: '#98FB98',
  light: '#3CB371',
  main: '#3CB371',
  dark: '#2E8B57',
  darker: '#006400',
  contrastText: '#FFFAFA',
};

// ----------------------------------------------------------------------

/**
 * Monta a paleta do MUI a partir de um conjunto de tokens.
 *
 * Recebe os tokens por parâmetro (em vez de ler `coresHex` direto) para que
 * o ThemeProvider possa declará-los como dependência do useMemo — assim o
 * tema é recriado quando, e somente quando, a paleta muda de verdade.
 */
export function criarPalette(tokens = coresHex) {
  const t = tokens;
  // PRIMARY — cor escura da marca
  // Usada em: navbar, sidebar, botões primários, cabeçalhos
  const PRIMARY = {
    lighter: clarear(t.chumbo, 0.89), // hover suave sobre fundo claro
    light: t.cinzaMedio, // cor neutra (30% da regra)
    main: t.chumbo, // cor escura (dominante da marca)
    dark: escurecer(t.chumbo, 0.435), // hover/pressed
    darker: escurecer(t.chumbo, 0.717), // quase preto (uso restrito)
    contrastText: t.branco,
  };

  // SECONDARY — cor primária da marca (destaque/ação)
  // Usada em: botões de ação secundária, badges, highlights, links ativos
  const SECONDARY = {
    lighter: t.laranjaClaro, // fundo de alertas/tags
    light: clarear(t.laranja, 0.45), // hover
    main: t.laranja,
    dark: escurecer(t.laranja, 0.235), // pressed
    darker: escurecer(t.laranja, 0.487),
    contrastText: t.textoSobrePrimaria, // branco ou escuro, conforme contraste
  };

  // ACCENT — variação mais saturada da cor primária (símbolo da logo)
  const ACCENT = {
    lighter: clarear(t.laranja, 0.88),
    light: clarear(t.laranja, 0.3),
    main: escurecer(t.laranja, 0.11),
    dark: escurecer(t.laranja, 0.36),
    darker: escurecer(t.laranja, 0.53),
    contrastText: t.branco,
  };

  // LIGHTGRAY — cinza de apoio
  const LIGHTGRAY = {
    lighter: '#F5F5F5',
    light: '#E0E0E0',
    main: '#C0C0C0',
    dark: t.cinzaMedio,
    darker: t.chumbo,
    contrastText: '#FFFAFA',
  };

  // ORANGE — alias da cor primária, para componentes que referenciam
  // theme.palette.orange
  const ORANGE = {
    lighter: t.laranjaClaro,
    light: clarear(t.laranja, 0.45),
    main: t.laranja,
    dark: escurecer(t.laranja, 0.235),
    darker: escurecer(t.laranja, 0.487),
    contrastText: t.branco,
  };

  return {
    common: { black: '#000', white: '#fff' },
    primary: PRIMARY,
    secondary: SECONDARY,
    accent: ACCENT,
    info: INFO,
    success: SUCCESS,
    warning: WARNING,
    error: ERROR,
    lightgreen: LIGHTGREEN,
    lightgray: LIGHTGRAY,
    orange: ORANGE,
    grey: GREY,
    divider: alpha(GREY[500], 0.24),
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: '#ffffff',
      default: '#F9FAFB',
      neutral: '#F4F6F8',
    },
    action: {
      active: t.cinzaMedio,
      hover: alpha(t.chumbo, 0.06),
      selected: alpha(t.laranja, 0.12), // seleção com toque da cor primária
      disabled: alpha(GREY[500], 0.8),
      disabledBackground: alpha(GREY[500], 0.24),
      focus: alpha(t.laranja, 0.16), // foco com a cor primária
      hoverOpacity: 0.06,
      disabledOpacity: 0.48,
    },
  };
}

// Export padrão mantido para compatibilidade: é a paleta com os valores
// vigentes no momento do import (os padrões TriAxis, no boot).
const palette = criarPalette();

export default palette;